/*
  Tray inner wizard (seva-dues-flow):
    loading → sign-in → select → contributions? → review → checkout → confirming → setup-autopay? → complete
                                  (review is enrollment-only; arrears skips straight to checkout)
              ↑ unavailable (API errors / mode: none if tray opened unexpectedly)
              └── seva:signed-out / invalid-session

    update-payment-method → update-payment-success → DUES_COMPLETE_DISMISS (skips loadOptions on entry)

  Pending bank payment (mode: pending_payment) is NOT a tray view — hand off to on-page CTA:
    loadOptions / checkout 409 → handOffPendingPaymentToCta()
      → destroyCheckout() → dispatch DUES_PENDING_STATUS → tray hide() + CTA reloadDuesState()

  Sign-in + pending_payment sequence:
    seva:authenticated
      ├─ CTA.onAuthenticated → loadOptions()
      └─ Flow.onAuthenticated → loadOptions() → handOffPendingPaymentToCta()
           → DUES_PENDING_STATUS → tray hide() + CTA reloadDuesState()

  select: membership type + optional frequency (period auto-selected from API)
  contributions step when options.voluntaryContributions.enabled (Plan 026)

  Local document events (not in SEVA_EVENTS):
    seva:dues-checkout-started / seva:dues-checkout-canceled — tray tracks checkoutActive
    seva:dues-header-change — flow updates tray drawer title per view
 */

import { LitElement, html, nothing, type PropertyValues } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import type { ClientErrorPhase } from '@seva/core';
import { SevaFreezable } from './lib/freezable-mixin.js';
import { duesFlowStyles } from './lib/dues-flow-styles.js';
import { ApiClient, ApiClientError } from './lib/api-client.js';
import { TokenManager } from './lib/token-manager.js';
import { bootAuthSession } from './lib/session.js';
import { classifyAuthError } from './lib/auth-errors.js';
import {
	formatCalendarDateNoWeekday,
	formatCents,
	formatVoluntaryContributionCampaignLabel
} from './lib/format.js';
import { SEVA_EVENTS } from './lib/events.js';
import { DEFAULT_API_URL } from './lib/constants.js';
import {
	DEFAULT_DUES_DRAWER_TITLE,
	DUES_HEADER_CHANGE,
	DUES_SELECT_WIZARD_TITLE,
	DUES_CONTRIBUTIONS_TITLE
} from './lib/dues-drawer.js';
import {
	acquireStripeLightDomMount,
	STRIPE_DUES_MIN_HEIGHT,
	STRIPE_MOUNT_SLOTS
} from './lib/stripe-light-dom-mount.js';
import { resolveDuesFlowInitialView } from './lib/dues-cta-state.js';
import type {
	DuesCheckoutPreviewResponse,
	DuesCheckoutResponse,
	DuesFrequencyOffer,
	DuesOptionsResponse,
	VoluntaryContributionsCheckoutSelection
} from './lib/dues-types.js';

type FlowView =
	| 'loading'
	| 'sign-in'
	| 'select'
	| 'contributions'
	| 'review'
	| 'checkout'
	| 'confirming'
	| 'setup-autopay'
	| 'update-payment-method'
	| 'update-payment-success'
	| 'complete'
	| 'unavailable';

type ContributionSelection = null | number | 'other';

type FlowStartView = 'sign-in' | 'update-payment-method';

/**
 * Card declines and input-validation errors from Stripe are expected user
 * outcomes, not failures worth a telemetry beacon / alert (Plan 038 follow-up).
 * Only genuine infrastructure errors (api/connection/etc.) should be reported.
 */
function isExpectedStripeError(type?: string): boolean {
	return type === 'card_error' || type === 'validation_error';
}

/** Per-installment interval suffix for the frequency cost detail (e.g. monthly → 'mo'). */
function intervalSuffix(id: string): string {
	switch (id) {
		case 'monthly':
			return 'mo';
		case 'quarterly':
			return 'qtr';
		case 'semiannual':
			return '6mo';
		default:
			return 'payment';
	}
}

@customElement('seva-dues-flow')
export class SevaDuesFlow extends SevaFreezable(LitElement) {
	static override styles = duesFlowStyles;

	@property({ attribute: 'tenant-slug' }) tenantSlug = '';
	@property({ attribute: 'api-url' }) apiUrl = DEFAULT_API_URL;
	@property({ attribute: 'return-url' }) returnUrl = '';
	@property() theme: 'light' | 'dark' = 'light';
	@property() presentation: 'tray' = 'tray';
	@property({ attribute: 'start-view' }) startView: FlowStartView | '' = '';
	@property({ attribute: 'update-dues-membership-id' }) updateDuesMembershipId = '';

	@state() private view: FlowView = 'loading';
	@state() private error = '';
	@state() private options: DuesOptionsResponse | null = null;
	@state() private selectedPeriodId = '';
	@state() private selectedMembershipTypeId = '';
	@state() private selectedPaymentFrequency = '';
	@state() private ereySelection: ContributionSelection = null;
	@state() private ereyOtherDollars = 10;
	@state() private sustainabilitySelection: ContributionSelection = null;
	@state() private sustainabilityOtherDollars = 10;
	@state() private previewResult: DuesCheckoutPreviewResponse | null = null;
	@state() private completedDuesMembershipId: string | null = null;
	@state() private loading = false;
	@state() private autopayReady = false;
	@state() private setupIntentId = '';

	private tokenManager!: TokenManager;
	private api!: ApiClient;
	private stripeInstance: unknown = null;
	private stripeCheckout: { destroy: () => void } | null = null;
	private stripeElements: unknown = null;
	private stripePaymentElement: { mount: (el: Element) => void; destroy: () => void } | null = null;
	private stripeMountRelease: (() => void) | null = null;
	private stripeAutopayMountRelease: (() => void) | null = null;
	private pollingTimer: ReturnType<typeof setTimeout> | null = null;
	private updateSuccessDismissTimer: ReturnType<typeof setTimeout> | null = null;
	private pollTimedOut = false;
	@state() private paymentProcessing = false;
	private loadGeneration = 0;
	private checkoutActive = false;

	connectedCallback(): void {
		super.connectedCallback();
		this.tokenManager = new TokenManager(`seva-auth-${this.tenantSlug}`);
		this.api = new ApiClient(this.apiUrl, this.tenantSlug, () => this.tokenManager.getToken());

		document.addEventListener('seva:authenticated', this.onAuthenticated);
		document.addEventListener('seva:signed-out', this.handleSignedOut);

		void this.init();
	}

	disconnectedCallback(): void {
		super.disconnectedCallback();
		document.removeEventListener('seva:authenticated', this.onAuthenticated);
		document.removeEventListener('seva:signed-out', this.handleSignedOut);
		if (this.updateSuccessDismissTimer) {
			clearTimeout(this.updateSuccessDismissTimer);
			this.updateSuccessDismissTimer = null;
		}
		this.destroyCheckout();
	}

	override updated(changedProperties: PropertyValues): void {
		if (
			changedProperties.has('view') ||
			changedProperties.has('options') ||
			changedProperties.has('selectedPeriodId') ||
			changedProperties.has('selectedMembershipTypeId')
		) {
			this.syncDrawerTitle();
		}
	}

	private resolveDrawerTitle(): string {
		if (this.view === 'update-payment-method' || this.view === 'update-payment-success') {
			return 'Update payment method';
		}
		if (this.view === 'contributions') {
			return DUES_CONTRIBUTIONS_TITLE;
		}
		if (this.view === 'review') {
			const total = this.contributionsEnabled ? 4 : 3;
			return `Renewal: Step ${total} of ${total}`;
		}
		if (this.view !== 'select' || this.options?.mode === 'arrears') {
			return DEFAULT_DUES_DRAWER_TITLE;
		}
		const frequencies = this.selectedType?.frequencies ?? [];
		return frequencies.length > 1 ? DUES_SELECT_WIZARD_TITLE : DEFAULT_DUES_DRAWER_TITLE;
	}

	private syncDrawerTitle(): void {
		this.dispatchEvent(
			new CustomEvent(DUES_HEADER_CHANGE, {
				bubbles: true,
				composed: true,
				detail: { title: this.resolveDrawerTitle() }
			})
		);
	}

	/** Called by <seva-dues-tray> on hide — tears down Stripe + poll timer. */
	destroyCheckout(): void {
		const wasActive = this.checkoutActive;
		this.destroyStripeUi();
		if (this.pollingTimer) {
			clearTimeout(this.pollingTimer);
			this.pollingTimer = null;
		}
		if (wasActive) {
			this.checkoutActive = false;
			document.dispatchEvent(
				new CustomEvent('seva:dues-checkout-canceled', { bubbles: true, composed: true })
			);
		}
	}

	private destroyStripeUi(): void {
		try {
			this.stripeCheckout?.destroy();
		} catch (e) {
			console.warn('[seva-dues-flow] Stripe checkout cleanup error:', e);
		}
		this.stripeCheckout = null;
		try {
			this.stripePaymentElement?.destroy();
		} catch (e) {
			console.warn('[seva-dues-flow] Stripe payment element cleanup error:', e);
		}
		this.stripePaymentElement = null;
		this.stripeElements = null;
		this.stripeMountRelease?.();
		this.stripeMountRelease = null;
		this.stripeAutopayMountRelease?.();
		this.stripeAutopayMountRelease = null;
	}

	private markCheckoutActive(): void {
		if (this.checkoutActive) return;
		this.checkoutActive = true;
		document.dispatchEvent(
			new CustomEvent('seva:dues-checkout-started', { bubbles: true, composed: true })
		);
	}

	private markCheckoutInactive(): void {
		if (!this.checkoutActive) return;
		this.checkoutActive = false;
		document.dispatchEvent(
			new CustomEvent('seva:dues-checkout-canceled', { bubbles: true, composed: true })
		);
	}

	private async init(): Promise<void> {
		await this.bootAuth();
		if (this.startView === 'sign-in') {
			this.view = 'sign-in';
			return;
		}
		if (this.startView === 'update-payment-method') {
			if (!this.updateDuesMembershipId) {
				this.view = 'unavailable';
				this.error = 'Unable to update payment method right now.';
				return;
			}
			this.view = 'update-payment-method';
			await this.mountAutopaySetup(this.updateDuesMembershipId);
			return;
		}
		await this.loadOptions();
	}

	private async bootAuth(): Promise<void> {
		const result = await bootAuthSession(this.tokenManager, this.api);
		if (result && 'user' in result) {
			document.dispatchEvent(
				new CustomEvent('seva:authenticated', {
					bubbles: true,
					composed: true,
					detail: { user: result.user, token: result.token }
				})
			);
		} else if (result && 'error' in result) {
			this.error = result.error;
		}
	}

	private handleSignedOut = (): void => {
		this.tokenManager.clear();
		this.options = null;
		this.error = '';
		this.selectedPeriodId = '';
		this.selectedMembershipTypeId = '';
		this.selectedPaymentFrequency = '';
		this.ereySelection = null;
		this.ereyOtherDollars = 10;
		this.sustainabilitySelection = null;
		this.sustainabilityOtherDollars = 10;
		this.completedDuesMembershipId = null;
		this.destroyCheckout();
		this.view = 'sign-in';
	};

	private onAuthenticated = (e: Event): void => {
		const detail = (e as CustomEvent).detail;
		if (detail?.token) {
			this.tokenManager.setAuth(detail.user, detail.token);
			// Update entry skips loadOptions — mode: none would map to unavailable and
			// wipe the update view (bootAuth also dispatches authenticated).
			if (
				this.startView === 'update-payment-method' ||
				this.view === 'update-payment-method' ||
				this.view === 'update-payment-success'
			) {
				return;
			}
			void this.loadOptions();
		}
	};

	private handleInvalidSession(): void {
		this.tokenManager.clear();
		document.dispatchEvent(new CustomEvent('seva:signed-out', { bubbles: true }));
		this.view = 'sign-in';
		this.error = '';
	}

	private defaultTypeForPeriod(periodId: string): string {
		const period = this.options?.periods.find((p) => p.id === periodId);
		if (!period || period.membershipTypes.length === 0) return '';
		// Use API array order (duesRate.sortOrder). Ignore defaultMembershipTypeId
		// so stale member-type defaults cannot win.
		return period.membershipTypes[0]?.id ?? '';
	}

	private preferredFrequencyId(frequencies: DuesFrequencyOffer[]): string {
		const annual = frequencies.find((f) => f.id === 'annual');
		return annual?.id ?? frequencies[0]?.id ?? 'annual';
	}

	private defaultFrequencyForType(typeId: string): string {
		const type = this.selectedPeriod?.membershipTypes.find((t) => t.id === typeId);
		return this.preferredFrequencyId(type?.frequencies ?? []);
	}

	private get selectedPeriod() {
		return this.options?.periods.find((p) => p.id === this.selectedPeriodId);
	}

	private get selectedType() {
		return this.selectedPeriod?.membershipTypes.find((t) => t.id === this.selectedMembershipTypeId);
	}

	private get selectedFrequencyOffer(): DuesFrequencyOffer | undefined {
		return this.selectedType?.frequencies?.find((f) => f.id === this.selectedPaymentFrequency);
	}

	private async loadOptions(): Promise<void> {
		if (
			this.startView === 'update-payment-method' ||
			this.view === 'update-payment-method' ||
			this.view === 'update-payment-success'
		) {
			return;
		}
		const gen = ++this.loadGeneration;
		const inConfirmingPoll = this.view === 'confirming';
		this.view = 'loading';
		this.error = '';
		if (!this.tokenManager.getToken()) {
			if (gen === this.loadGeneration) this.view = 'sign-in';
			return;
		}
		try {
			const data = await this.api.getDuesOptions();
			if (gen !== this.loadGeneration) return;
			if (data.mode === 'pending_payment') {
				if (inConfirmingPoll) {
					this.view = 'confirming';
					return;
				}
				this.handOffPendingPaymentToCta();
				return;
			}
			const initialView = resolveDuesFlowInitialView(data);
			if (initialView === 'unavailable') {
				this.view = 'unavailable';
				return;
			}
			this.options = data;
			this.selectedPeriodId = data.recommendedPeriodId ?? data.periods[0]?.id ?? '';
			this.selectedMembershipTypeId = this.defaultTypeForPeriod(this.selectedPeriodId);
			this.selectedPaymentFrequency = this.defaultFrequencyForType(this.selectedMembershipTypeId);
			this.view = 'select';
		} catch (e) {
			if (gen !== this.loadGeneration) return;
			const verdict = classifyAuthError(e);
			if (verdict.kind === 'invalid-session') {
				this.handleInvalidSession();
				return;
			}
			this.error = verdict.message;
			this.view = 'unavailable';
		}
	}

	private get contributionsEnabled(): boolean {
		return this.options?.voluntaryContributions?.enabled === true;
	}

	private get wizardStepCount(): number {
		return this.contributionsEnabled ? 2 : 1;
	}

	private get duesDueTodayCents(): number {
		const freq = this.selectedFrequencyOffer;
		if (freq) return freq.firstChargeCents;
		return this.selectedType?.annualAmountCents ?? 0;
	}

	private get duesDueTodayLabel(): string {
		const freq = this.selectedFrequencyOffer;
		const count = freq?.firstChargeInstallmentCount ?? 1;
		return count > 1 ? `Dues payment (${count} installments)` : 'Dues payment';
	}

	private resolveContributionAmountCents(section: 'erey' | 'sustainability'): number | null {
		const selection = section === 'erey' ? this.ereySelection : this.sustainabilitySelection;
		if (selection == null) return null;
		if (selection === 'other') {
			const dollars = section === 'erey' ? this.ereyOtherDollars : this.sustainabilityOtherDollars;
			const minDollars = Math.ceil(
				(this.options?.voluntaryContributions?.minAmountCents ?? 1000) / 100
			);
			return Math.max(minDollars, Math.floor(dollars)) * 100;
		}
		return selection;
	}

	private get contributionsDueTodayCents(): number {
		return (
			(this.resolveContributionAmountCents('erey') ?? 0) +
			(this.resolveContributionAmountCents('sustainability') ?? 0)
		);
	}

	private buildVoluntaryContributionsPayload():
		| VoluntaryContributionsCheckoutSelection
		| undefined {
		const ereyAmountCents = this.resolveContributionAmountCents('erey');
		const sustainabilityAmountCents = this.resolveContributionAmountCents('sustainability');
		if (ereyAmountCents == null && sustainabilityAmountCents == null) {
			return undefined;
		}
		const payload: VoluntaryContributionsCheckoutSelection = {};
		if (ereyAmountCents != null) payload.ereyAmountCents = ereyAmountCents;
		if (sustainabilityAmountCents != null) {
			payload.sustainabilityAmountCents = sustainabilityAmountCents;
		}
		return payload;
	}

	private clearContributionSelections(): void {
		this.ereySelection = null;
		this.sustainabilitySelection = null;
		this.ereyOtherDollars = 10;
		this.sustainabilityOtherDollars = 10;
	}

	private togglePresetContribution(section: 'erey' | 'sustainability', amount: number): void {
		const current = section === 'erey' ? this.ereySelection : this.sustainabilitySelection;
		if (current === amount) {
			if (section === 'erey') this.ereySelection = null;
			else this.sustainabilitySelection = null;
			return;
		}
		if (section === 'erey') this.ereySelection = amount;
		else this.sustainabilitySelection = amount;
	}

	private toggleOtherContribution(section: 'erey' | 'sustainability'): void {
		const current = section === 'erey' ? this.ereySelection : this.sustainabilitySelection;
		if (current === 'other') {
			if (section === 'erey') this.ereySelection = null;
			else this.sustainabilitySelection = null;
			return;
		}
		if (section === 'erey') this.ereySelection = 'other';
		else this.sustainabilitySelection = 'other';
	}

	private formatFrequencyDisplayLabel(freq: DuesFrequencyOffer): string {
		return freq.id === 'annual' ? 'Pay in full' : freq.label;
	}

	private formatFrequencyCostDetail(freq: DuesFrequencyOffer): string {
		if (freq.installmentCount <= 1) {
			return `${formatCents(freq.totalAmountCents)}/yr`;
		}
		const per = intervalSuffix(freq.id);
		return `${formatCents(freq.firstPaymentCents)}/${per} × ${freq.installmentCount} · ${formatCents(freq.totalAmountCents)}/yr`;
	}

	/**
	 * `'2026-07-01' → 'Jul 2026'`. Parses the YYYY-MM-DD string as a local date
	 * (not `new Date('2026-07-01')`, which is UTC and can render the previous
	 * month for viewers in negative-offset timezones).
	 */
	private formatDueMonth(dateStr: string): string {
		const [year, month, day] = dateStr.split('-').map(Number);
		const local = new Date(year, (month ?? 1) - 1, day ?? 1);
		return new Intl.DateTimeFormat('en-US', { month: 'short', year: 'numeric' }).format(local);
	}

	private renderReviewStep() {
		const p = this.previewResult;
		if (!p) return nothing;
		const covered = p.firstPaymentBreakdown.coveredInstallments;
		const goForward = p.firstPaymentBreakdown.goForward;
		const ereyAmountCents = this.resolveContributionAmountCents('erey');
		const sustainabilityAmountCents = this.resolveContributionAmountCents('sustainability');
		const ereyLabel = formatVoluntaryContributionCampaignLabel('erey');
		const sustainabilityLabel = formatVoluntaryContributionCampaignLabel('sustainability');

		return html`<div class="card wizard-step">
			<h2 class="dues-heading">Review and confirm your first payment</h2>
			<div class="contributions-summary">
				<table class="summary-table" aria-label="First payment breakdown">
					<tbody>
						${covered.map(
							(c) =>
								html`<tr data-testid="review-covered-row">
									<td class="label">Membership dues — ${this.formatDueMonth(c.dueDate)}</td>
									<td class="amount">${formatCents(c.amountCents)}</td>
								</tr>`
						)}
						${p.initiationFeeCents != null && p.initiationFeeCents > 0
							? html`<tr data-testid="review-one-time-fee-row">
									<td class="label">${p.oneTimeFeeLabel ?? 'Initiation fee'}</td>
									<td class="amount">${formatCents(p.initiationFeeCents)}</td>
								</tr>`
							: nothing}
						${sustainabilityAmountCents != null
							? html`<tr data-testid="review-contribution-row">
									<td class="label">${sustainabilityLabel}</td>
									<td class="amount">${formatCents(sustainabilityAmountCents)}</td>
								</tr>`
							: nothing}
						${ereyAmountCents != null
							? html`<tr data-testid="review-contribution-row">
									<td class="label">${ereyLabel}</td>
									<td class="amount">${formatCents(ereyAmountCents)}</td>
								</tr>`
							: nothing}
						<tr class="total-row">
							<td colspan="2" class="total-cell" data-testid="review-total">
								Total due today <span>${formatCents(p.totalAmountCents)}</span>
							</td>
						</tr>
					</tbody>
				</table>
			</div>
			${goForward
				? html`<p class="review-go-forward" data-testid="review-go-forward">
						Then ${formatCents(goForward.installmentAmountCents)}/payment starting
						${this.formatDueMonth(goForward.nextDueDate)} — ${goForward.remainingCount} payments.
					</p>`
				: nothing}
			${this.renderErrorAlert()}
			<div class="review-actions step-actions">
				<button class="btn-outline" data-testid="review-back" @click=${this.backFromReview}>
					Back
				</button>
				<button
					class="btn-primary"
					data-testid="review-continue"
					?disabled=${this.loading}
					@click=${this.continueFromReview}
				>
					Continue to payment
				</button>
			</div>
		</div>`;
	}

	private advanceToContributionsOrCheckout(): void {
		if (this.contributionsEnabled) {
			this.view = 'contributions';
			return;
		}
		void this.openReview();
	}

	/**
	 * Membership/frequency/contribution assembly shared by the preview call and the
	 * real checkout so the two cannot drift. `startCheckout` layers `returnUrl` on
	 * top; the preview body omits it (no Stripe session to return to).
	 */
	private buildDuesCheckoutBody(): {
		duesPeriodId: string;
		membershipTypeId?: string;
		paymentFrequency?: string;
		voluntaryContributions?: VoluntaryContributionsCheckoutSelection;
	} {
		const body: {
			duesPeriodId: string;
			membershipTypeId?: string;
			paymentFrequency?: string;
			voluntaryContributions?: VoluntaryContributionsCheckoutSelection;
		} = { duesPeriodId: this.selectedPeriodId };
		if (this.options && this.options.mode !== 'arrears') {
			body.membershipTypeId = this.selectedMembershipTypeId;
			body.paymentFrequency = this.selectedPaymentFrequency || 'annual';
			const vc = this.buildVoluntaryContributionsPayload();
			if (vc) body.voluntaryContributions = vc;
		}
		return body;
	}

	/**
	 * Every enrollment entry point funnels through here so the member always sees
	 * the first-charge review before a Stripe session is created. Arrears has no
	 * breakdown and short-circuits straight to checkout (today's behavior).
	 */
	private async openReview(): Promise<void> {
		if (!this.options || !this.selectedPeriodId) return;
		if (this.options.mode === 'arrears') {
			void this.startCheckout();
			return;
		}
		this.loading = true;
		this.error = '';
		try {
			this.previewResult = await this.api.previewDuesCheckout(this.buildDuesCheckoutBody());
			this.view = 'review';
		} catch (e) {
			const verdict = classifyAuthError(e);
			if (verdict.kind === 'invalid-session') {
				this.handleInvalidSession();
				return;
			}
			this.error = verdict.message || 'Could not load your payment summary';
		} finally {
			this.loading = false;
		}
	}

	private continueFromReview(): void {
		void this.startCheckout();
	}

	private backFromReview(): void {
		this.view = this.contributionsEnabled ? 'contributions' : 'select';
	}

	private advanceFromType(): void {
		const frequencies = this.selectedType?.frequencies ?? [];
		this.selectedPaymentFrequency =
			this.selectedPaymentFrequency || this.preferredFrequencyId(frequencies);
		if (frequencies.length <= 1) {
			this.selectedPaymentFrequency = frequencies[0]?.id ?? 'annual';
		}
		this.advanceToContributionsOrCheckout();
	}

	private async startCheckout(): Promise<void> {
		if (!this.options || !this.selectedPeriodId) return;
		this.loading = true;
		this.error = '';
		try {
			const body = {
				...this.buildDuesCheckoutBody(),
				returnUrl: this.returnUrl || window.location.href
			};

			const result = await this.api.createDuesCheckout(body);
			await this.mountStripeCheckout(result);
		} catch (e) {
			if (
				e instanceof ApiClientError &&
				e.status === 409 &&
				e.code === 'dues_payment_in_flight' &&
				e.pendingPayment
			) {
				this.handOffPendingPaymentToCta();
				this.error = '';
				return;
			}
			const verdict = classifyAuthError(e);
			if (verdict.kind === 'invalid-session') {
				this.handleInvalidSession();
				return;
			}
			this.error = verdict.message || 'Checkout failed';
			this.dispatchEvent(
				new CustomEvent('seva:error', {
					bubbles: true,
					composed: true,
					detail: { message: this.error }
				})
			);
		} finally {
			this.loading = false;
		}
	}

	private async mountStripeCheckout(result: DuesCheckoutResponse): Promise<void> {
		let phase: ClientErrorPhase = 'stripe_js_load_failed';
		try {
			const { loadStripe } = await import('@stripe/stripe-js/pure');
			this.stripeInstance = await loadStripe(result.stripePublishableKey, {
				stripeAccount: result.stripeAccountId
			});
			if (!this.stripeInstance) {
				throw new Error('Stripe failed to load — check publishable key and connected account');
			}

			this.view = 'checkout';
			this.markCheckoutActive();
			await this.updateComplete;

			this.destroyStripeUi();
			const { element, release } = acquireStripeLightDomMount(this, STRIPE_MOUNT_SLOTS.CHECKOUT, {
				minHeight: STRIPE_DUES_MIN_HEIGHT
			});
			this.stripeMountRelease = release;

			const stripe = this.stripeInstance as {
				initEmbeddedCheckout: (opts: {
					clientSecret: string;
					onComplete?: () => void;
				}) => Promise<{ mount: (el: Element) => void; destroy: () => void }>;
			};
			phase = 'embedded_init_failed';
			const checkout = await stripe.initEmbeddedCheckout({
				clientSecret: result.clientSecret,
				onComplete: () => {
					this.handleCheckoutComplete(result.checkoutSessionId);
				}
			});
			this.stripeCheckout = checkout;
			phase = 'mount_failed';
			checkout.mount(element);
		} catch (e) {
			this.reportDuesClientError(phase, e, {
				stripeSessionId: result.checkoutSessionId,
				amountCents: result.totalAmountCents ?? result.amountCents
			});
			this.destroyStripeUi();
			throw e;
		}
	}

	private reportDuesClientError(
		phase: ClientErrorPhase,
		err?: unknown,
		extra?: { stripeSessionId?: string; amountCents?: number }
	): void {
		const message =
			err instanceof Error
				? err.message
				: typeof err === 'string'
					? err
					: phase === 'poll_timeout'
						? 'Dues checkout status poll timed out after 60s'
						: 'Dues client error';
		const name = err instanceof Error ? err.name : undefined;
		this.api.reportClientError({
			feature: 'dues',
			phase,
			message,
			name,
			stripeSessionId: extra?.stripeSessionId,
			amountCents: extra?.amountCents
		});
	}

	private dispatchCheckoutComplete(): void {
		document.dispatchEvent(
			new CustomEvent(SEVA_EVENTS.DUES_CHECKOUT_COMPLETE, {
				bubbles: true,
				composed: true
			})
		);
	}

	private handleCheckoutComplete(checkoutSessionId: string): void {
		this.destroyStripeUi();
		this.error = '';
		this.pollTimedOut = false;
		this.paymentProcessing = false;
		this.view = 'confirming';
		this.pollCheckoutStatus(checkoutSessionId, 0, 0);
	}

	private pollCheckoutStatus(sessionId: string, delay: number, elapsed: number): void {
		if (!this.paymentProcessing && elapsed >= 60000) {
			this.pollTimedOut = true;
			this.error =
				'Payment received but confirmation is taking longer than expected. Refresh in a minute or contact your club.';
			// Bank-aware: while paymentProcessing / awaiting_payment the 60s cap is skipped above.
			this.reportDuesClientError('poll_timeout', undefined, { stripeSessionId: sessionId });
			return;
		}

		this.pollingTimer = setTimeout(async () => {
			try {
				const status = await this.api.getDuesCheckoutStatus(sessionId);

				if (status.checkoutFailed || status.status === 'failed') {
					this.paymentProcessing = false;
					this.error = 'Your payment could not be completed. Please try again.';
					return;
				}

				if (status.paymentProcessing || status.status === 'awaiting_payment') {
					this.paymentProcessing = true;
				}

				if (status.status === 'completed') {
					this.paymentProcessing = false;
					this.completedDuesMembershipId = status.duesMembershipId ?? null;
					if (status.autopayEnabled) {
						this.view = 'complete';
						this.markCheckoutInactive();
						this.dispatchCheckoutComplete();
					} else if (status.autopaySetupRequired && this.completedDuesMembershipId) {
						this.view = 'setup-autopay';
						this.markCheckoutInactive();
						await this.mountAutopaySetup(this.completedDuesMembershipId);
					} else {
						this.view = 'complete';
						this.markCheckoutInactive();
						this.dispatchCheckoutComplete();
					}
					return;
				}
			} catch {
				// Keep polling — webhook may still be in flight.
			}
			const maxDelay = this.paymentProcessing ? 60000 : 8000;
			const nextDelay = Math.min(Math.max(delay * 2, 1000), maxDelay);
			this.pollCheckoutStatus(sessionId, nextDelay, elapsed + delay);
		}, delay);
	}

	private async mountAutopaySetup(duesMembershipId: string): Promise<void> {
		if (!duesMembershipId) return;
		this.loading = true;
		this.autopayReady = false;
		this.error = '';
		try {
			const setup = await this.api.setupDuesAutopay({ duesMembershipId });
			const { loadStripe } = await import('@stripe/stripe-js/pure');
			this.stripeInstance = await loadStripe(setup.stripePublishableKey, {
				stripeAccount: setup.stripeAccountId
			});
			if (!this.stripeInstance) {
				throw new Error('Stripe failed to load');
			}

			await this.updateComplete;

			this.destroyStripeUi();
			this.markCheckoutActive();
			const { element, release } = acquireStripeLightDomMount(this, STRIPE_MOUNT_SLOTS.AUTOPAY, {
				minHeight: STRIPE_DUES_MIN_HEIGHT
			});
			this.stripeAutopayMountRelease = release;

			const stripe = this.stripeInstance as {
				elements: (opts: { clientSecret: string; wallets?: { link?: 'never' | 'auto' } }) => {
					create: (type: string) => { mount: (el: Element) => void; destroy: () => void };
				};
				confirmSetup: (opts: {
					elements: unknown;
					redirect: 'if_required';
				}) => Promise<{ error?: { message?: string } }>;
			};
			this.stripeElements = stripe.elements({
				clientSecret: setup.clientSecret,
				wallets: { link: 'never' }
			});
			const paymentElement = (
				this.stripeElements as {
					create: (type: string) => { mount: (el: Element) => void; destroy: () => void };
				}
			).create('payment');
			if (!paymentElement) throw new Error('Unable to create payment element');
			this.stripePaymentElement = paymentElement;
			paymentElement.mount(element);
			this.autopayReady = true;
			this.setupIntentId = setup.setupIntentId;
		} catch (e) {
			this.destroyStripeUi();
			const verdict = classifyAuthError(e);
			if (verdict.kind === 'invalid-session') {
				// Expired session is expected and handled — don't beacon / alert.
				this.handleInvalidSession();
				return;
			}
			this.reportDuesClientError('autopay_setup_load_failed', e);
			this.error = verdict.message || 'Unable to start autopay setup';
		} finally {
			this.loading = false;
		}
	}

	private resolveAutopayDuesMembershipId(): string | null {
		return this.updateDuesMembershipId || this.completedDuesMembershipId;
	}

	private async confirmAutopay(): Promise<void> {
		const duesMembershipId = this.resolveAutopayDuesMembershipId();
		if (!duesMembershipId || !this.stripeElements || !this.stripeInstance) return;
		const fromUpdateView = this.view === 'update-payment-method';
		this.loading = true;
		this.error = '';
		try {
			const stripe = this.stripeInstance as {
				confirmSetup: (opts: {
					elements: unknown;
					redirect: 'if_required';
				}) => Promise<{ error?: { message?: string; type?: string } }>;
			};
			const { error: stripeError } = await stripe.confirmSetup({
				elements: this.stripeElements,
				redirect: 'if_required'
			});
			if (stripeError) {
				// Beacon only genuine failures — card declines / validation errors are
				// expected user outcomes and would otherwise flood #payments-alerts.
				if (!isExpectedStripeError(stripeError.type)) {
					this.reportDuesClientError(
						'autopay_setup_confirm_failed',
						stripeError.message ?? 'Payment method setup failed'
					);
				}
				this.error = stripeError.message ?? 'Payment method setup failed';
				return;
			}

			if (!this.setupIntentId) {
				this.error = 'Setup session expired — refresh and try again';
				return;
			}

			await this.api.confirmDuesAutopay({
				duesMembershipId,
				setupIntentId: this.setupIntentId
			});
			this.destroyStripeUi();
			if (fromUpdateView) {
				this.view = 'update-payment-success';
				if (this.updateSuccessDismissTimer) clearTimeout(this.updateSuccessDismissTimer);
				this.updateSuccessDismissTimer = setTimeout(() => {
					this.dismissComplete();
				}, 3000);
			} else {
				this.view = 'complete';
				this.dispatchCheckoutComplete();
			}
		} catch (e) {
			const verdict = classifyAuthError(e);
			if (verdict.kind === 'invalid-session') {
				// Expired session is expected and handled — don't beacon / alert.
				this.handleInvalidSession();
				return;
			}
			this.reportDuesClientError('autopay_setup_confirm_failed', e);
			this.error = verdict.message || 'Unable to save payment method';
		} finally {
			this.loading = false;
		}
	}

	private renderUpdatePaymentSuccess() {
		return html`<div class="card">
			<div class="success-icon">&#10003;</div>
			<h2 class="confirmation-heading">Payment method updated</h2>
			<div class="actions" style="margin-top: 1rem; text-align: center;">
				<button class="btn-outline" @click=${this.dismissComplete}>Done</button>
			</div>
		</div>`;
	}

	private renderUpdatePaymentMethodStep() {
		return html`<div class="card checkout-card checkout-card-autopay">
			<div class="autopay-scroll">
				<h2 class="dues-heading">Update payment method</h2>
				<p class="dues-subheading">
					We'll use this for your remaining installment payments. Autopay stays on.
				</p>
				<slot name="stripe-autopay"></slot>
				${this.renderErrorAlert()}
			</div>
			<div class="autopay-actions">
				<button
					class="btn-primary"
					?disabled=${this.loading || !this.autopayReady}
					@click=${this.confirmAutopay}
				>
					${this.loading ? 'Saving…' : 'Save payment method'}
				</button>
			</div>
		</div>`;
	}

	private renderErrorAlert() {
		return this.error ? html`<div class="alert alert-error">${this.error}</div>` : nothing;
	}

	private handOffPendingPaymentToCta(): void {
		if (this.view === 'confirming') return;
		this.destroyCheckout();
		document.dispatchEvent(
			new CustomEvent(SEVA_EVENTS.DUES_PENDING_STATUS, { bubbles: true, composed: true })
		);
	}

	private dismissComplete(): void {
		document.dispatchEvent(
			new CustomEvent(SEVA_EVENTS.DUES_COMPLETE_DISMISS, { bubbles: true, composed: true })
		);
	}

	private renderConfirmingStep() {
		const bankProcessing = this.paymentProcessing;
		const heading = bankProcessing ? 'Bank payment processing…' : 'Confirming your payment…';
		const detail = bankProcessing
			? 'Your payment is being confirmed with your bank. This can take a few minutes — sometimes longer. Please keep this window open; we will update automatically when it clears.'
			: 'Please keep this window open. This usually takes just a few seconds while we confirm your payment.';

		return html`<div class="card confirming-card">
			<div class="confirming-state" role="status" aria-live="polite">
				<span class="spinner confirming-spinner" aria-hidden="true"></span>
				<h2 class="confirming-heading">${heading}</h2>
				<p class="confirming-detail">${detail}</p>
			</div>
			${this.renderErrorAlert()}
		</div>`;
	}

	private renderComplete() {
		return html`<div class="card">
			<div class="success-icon">&#10003;</div>
			<h2 class="confirmation-heading">Dues received</h2>
			<div class="confirmation-details">
				<p>
					Your membership commitment has been confirmed and we have received your payment. You will
					receive a confirmation email shortly.
				</p>
			</div>
			<div class="actions" style="margin-top: 1rem; text-align: center;">
				<button class="btn-outline" @click=${this.dismissComplete}>Dismiss</button>
			</div>
		</div>`;
	}

	private renderSignIn() {
		import('./seva-auth.js');
		return html`
			<seva-auth
				tenant-slug=${this.tenantSlug}
				api-url=${this.apiUrl}
				theme=${this.theme}
				hide-sign-up
			></seva-auth>
		`;
	}

	private renderTypeButtons(types: Array<{ id: string; name: string; annualAmountCents: number }>) {
		return html`
			<div
				class="method-picker membership-type-list"
				role="radiogroup"
				aria-label="Membership type"
			>
				${types.map(
					(type) => html`
						<button
							type="button"
							class="btn-method"
							role="radio"
							aria-checked=${this.selectedMembershipTypeId === type.id ? 'true' : 'false'}
							@click=${() => {
								this.selectedMembershipTypeId = type.id;
								this.selectedPaymentFrequency = this.defaultFrequencyForType(type.id);
							}}
						>
							<span class="type-name"
								>${type.name}
								<span class="type-price">(${formatCents(type.annualAmountCents)})</span></span
							>
						</button>
					`
				)}
			</div>
		`;
	}

	private renderFrequencySelect(frequencies: DuesFrequencyOffer[]) {
		return html`
			<select
				id="dues-frequency"
				@change=${(e: Event) => {
					this.selectedPaymentFrequency = (e.target as HTMLSelectElement).value;
				}}
			>
				${frequencies.map(
					(freq) => html`
						<option value=${freq.id} ?selected=${freq.id === this.selectedPaymentFrequency}>
							${this.formatFrequencyDisplayLabel(freq)}
						</option>
					`
				)}
			</select>
		`;
	}

	private renderArrearsSelect(period: NonNullable<DuesOptionsResponse['periods'][number]>) {
		const lockedType = period.membershipTypes[0];
		return html`<div class="card wizard-step">
			<div class="field">
				<label>Period</label>
				<div class="arrears-type">${period.name}</div>
			</div>
			<div class="field field-spaced">
				<label>Membership type</label>
				${lockedType
					? html`<div class="arrears-type">
							<span class="type-name">${lockedType.name}</span>
							<span class="type-price"
								>${formatCents(lockedType.totalAmountCents ?? 0)} due now</span
							>
						</div>`
					: nothing}
			</div>
			${this.renderErrorAlert()}
			<div class="step-actions step-actions-single">
				<button class="btn-primary" ?disabled=${this.loading} @click=${this.startCheckout}>
					${this.loading ? 'Starting…' : 'Continue to payment'}
				</button>
			</div>
		</div>`;
	}

	private renderContributionAmountPicker(section: 'erey' | 'sustainability', ariaLabel: string) {
		const presetAmounts = this.options?.voluntaryContributions?.presetAmountsCents ?? [
			2500, 5000, 10000
		];
		const selection = section === 'erey' ? this.ereySelection : this.sustainabilitySelection;
		const otherDollars =
			section === 'erey' ? this.ereyOtherDollars : this.sustainabilityOtherDollars;
		const otherFieldId = `${section}-other-field`;
		const minDollars = Math.ceil(
			(this.options?.voluntaryContributions?.minAmountCents ?? 1000) / 100
		);

		return html`
			<div class="amount-picker" role="group" aria-label=${ariaLabel}>
				${presetAmounts.map(
					(amount) => html`
						<button
							type="button"
							class="btn-method"
							aria-pressed=${selection === amount ? 'true' : 'false'}
							@click=${() => this.togglePresetContribution(section, amount)}
						>
							${formatCents(amount)}
						</button>
					`
				)}
				<button
					type="button"
					class="btn-method"
					aria-pressed=${selection === 'other' ? 'true' : 'false'}
					@click=${() => this.toggleOtherContribution(section)}
				>
					Other
				</button>
			</div>
			<div
				id=${otherFieldId}
				class="other-field ${selection === 'other' ? 'visible' : ''}"
				?hidden=${selection !== 'other'}
			>
				<label>
					<span class="visually-hidden">Custom amount</span>
					<input
						type="number"
						min=${minDollars}
						step="1"
						inputmode="numeric"
						.value=${String(otherDollars)}
						@input=${(e: Event) => {
							const value = Number.parseInt((e.target as HTMLInputElement).value, 10);
							if (section === 'erey') {
								this.ereyOtherDollars = Number.isNaN(value) ? minDollars : value;
							} else {
								this.sustainabilityOtherDollars = Number.isNaN(value) ? minDollars : value;
							}
						}}
					/>
				</label>
				<span class="other-hint">$${minDollars} minimum contribution</span>
			</div>
		`;
	}

	private renderContributionsStep() {
		const totalDueToday = this.duesDueTodayCents + this.contributionsDueTodayCents;
		const ereyLabel = formatVoluntaryContributionCampaignLabel('erey');
		const sustainabilityLabel = formatVoluntaryContributionCampaignLabel('sustainability');
		const ereyAmountCents = this.resolveContributionAmountCents('erey');
		const sustainabilityAmountCents = this.resolveContributionAmountCents('sustainability');

		return html`<div class="card wizard-step">
			<p class="contributions-intro">
				One-time voluntary contributions are completely optional, and do not affect your membership
				standing or installment schedule.
			</p>

			<div class="field contributions-section">
				<span class="contributions-label">Support your Rotary Club</span>
				<p class="contributions-helper">
					Contribute to the Rotary Club of San Antonio's Sustainability Fund.
				</p>
				${this.renderContributionAmountPicker(
					'sustainability',
					'Club sustainability contribution amount'
				)}
			</div>

			<div class="field contributions-section contributions-section-erey">
				<span class="contributions-label">Support Rotary International</span>
				<p class="contributions-helper">Contribute to Rotary's Annual Fund.</p>
				<div class="contribution-controls-spaced">
					${this.renderContributionAmountPicker('erey', 'EREY contribution amount')}
				</div>
			</div>

			<div class="contributions-summary">
				<table class="summary-table" aria-label="Amount due today">
					<tbody>
						<tr>
							<td class="label">${this.duesDueTodayLabel}</td>
							<td class="amount">${formatCents(this.duesDueTodayCents)}</td>
						</tr>
						${sustainabilityAmountCents != null
							? html`<tr>
									<td class="label">${sustainabilityLabel}</td>
									<td class="amount">${formatCents(sustainabilityAmountCents)}</td>
								</tr>`
							: nothing}
						${ereyAmountCents != null
							? html`<tr>
									<td class="label">${ereyLabel}</td>
									<td class="amount">${formatCents(ereyAmountCents)}</td>
								</tr>`
							: nothing}
						<tr class="total-row">
							<td colspan="2" class="total-cell">
								Total due today <span>${formatCents(totalDueToday)}</span>
							</td>
						</tr>
					</tbody>
				</table>
			</div>

			${this.renderErrorAlert()}
			<div class="step-actions">
				<button class="btn-outline" @click=${() => (this.view = 'select')}>Back</button>
				<button
					class="btn-primary"
					?disabled=${this.loading}
					@click=${() => void this.openReview()}
				>
					${this.loading ? 'Starting…' : 'Continue to review'}
				</button>
			</div>
			<button
				class="btn-link contributions-skip"
				type="button"
				?disabled=${this.loading}
				@click=${() => {
					this.clearContributionSelections();
					void this.openReview();
				}}
			>
				Continue without contributions
			</button>
		</div>`;
	}

	private renderSelectWizard() {
		const period = this.selectedPeriod;
		if (!period) return nothing;

		const today = new Intl.DateTimeFormat('en-CA', {
			timeZone: this.options?.timezone ?? 'UTC',
			year: 'numeric',
			month: '2-digit',
			day: '2-digit'
		}).format(new Date());
		const frequencies = this.selectedType?.frequencies ?? [];
		const showFrequency = frequencies.length > 1;
		const showInstallmentFeeHint = frequencies.some((f) => f.financingFeeCents > 0);
		const showAutopayDisclosure =
			showFrequency &&
			this.selectedFrequencyOffer != null &&
			this.selectedFrequencyOffer.installmentCount > 1;
		const hasFutureCoverage = this.selectedType != null && this.selectedType.coverageStart > today;
		const showDueNow = showFrequency && this.selectedFrequencyOffer;
		const coverageHint = hasFutureCoverage
			? html`<span class="dues-coverage dues-coverage-hint"
					>Member term begins ${formatCalendarDateNoWeekday(this.selectedType!.coverageStart)}</span
				>`
			: nothing;
		const frequencyCostDetail =
			showFrequency && this.selectedFrequencyOffer
				? html`<p class="frequency-cost-detail">
						${this.formatFrequencyCostDetail(this.selectedFrequencyOffer)}
					</p>`
				: nothing;
		const paymentSummary = showDueNow
			? html`<div class="dues-summary">
					<p class="dues-total">Due now: ${formatCents(this.duesDueTodayCents)}</p>
				</div>`
			: nothing;
		const autopayDisclosure = showAutopayDisclosure
			? html`<p class="autopay-disclosure">
					Note: Future installments will be charged automatically to the same card or bank account
					you use today.
				</p>`
			: nothing;

		return html`<div class="card wizard-step">
			<div class="field field-membership-type">
				<label>1. Select a membership type</label>
				${coverageHint} ${this.renderTypeButtons(period.membershipTypes)}
			</div>
			${showFrequency
				? html`<div class="field field-spaced field-payment-frequency">
						<label for="dues-frequency">2. Select a payment frequency</label>
						${showInstallmentFeeHint
							? html`<span class="frequency-fee-hint">*Fees may apply to installment plans</span>`
							: nothing}
						${this.renderFrequencySelect(frequencies)} ${frequencyCostDetail} ${autopayDisclosure}
						${paymentSummary}
					</div>`
				: showDueNow
					? html`<div class="field-spaced">${paymentSummary}</div>`
					: nothing}
			${this.renderErrorAlert()}
			<div class="step-actions step-actions-single">
				<button
					class="btn-primary"
					?disabled=${this.loading || !this.selectedMembershipTypeId}
					@click=${this.advanceFromType}
				>
					${this.loading
						? 'Starting…'
						: this.contributionsEnabled
							? 'Continue'
							: 'Continue to review'}
				</button>
			</div>
		</div>`;
	}

	override render() {
		if (this.view === 'loading') {
			return html`<div class="card">Loading dues…</div>`;
		}
		if (this.view === 'sign-in') {
			return html`${this.renderErrorAlert()} ${this.renderSignIn()}`;
		}
		if (this.view === 'unavailable') {
			return html`<div class="card">
				<p>No dues payment is available right now.</p>
				${this.renderErrorAlert()}
			</div>`;
		}
		if (this.view === 'complete') {
			return this.renderComplete();
		}
		if (this.view === 'update-payment-success') {
			return this.renderUpdatePaymentSuccess();
		}
		if (this.view === 'update-payment-method') {
			return this.renderUpdatePaymentMethodStep();
		}
		if (this.view === 'confirming') {
			return this.renderConfirmingStep();
		}
		if (this.view === 'contributions') {
			return this.renderContributionsStep();
		}
		if (this.view === 'review') {
			return this.renderReviewStep();
		}
		if (this.view === 'checkout') {
			return html`<div class="card checkout-card"><slot name="stripe-checkout"></slot></div>`;
		}
		if (this.view === 'setup-autopay') {
			return html`<div class="card checkout-card checkout-card-autopay">
				<div class="autopay-scroll">
					<h2 class="dues-heading">Set up autopay</h2>
					<p class="dues-subheading">Save a payment method for future installments.</p>
					<slot name="stripe-autopay"></slot>
					${this.renderErrorAlert()}
				</div>
				<div class="autopay-actions">
					<button
						class="btn-primary"
						?disabled=${this.loading || !this.autopayReady}
						@click=${this.confirmAutopay}
					>
						${this.loading ? 'Saving…' : 'Save payment method for autopay'}
					</button>
				</div>
			</div>`;
		}

		if (this.options?.mode === 'arrears') {
			const period = this.selectedPeriod;
			if (!period) return nothing;
			return this.renderArrearsSelect(period);
		}

		return this.renderSelectWizard();
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'seva-dues-flow': SevaDuesFlow;
	}
}
